import { equals } from 'ramda';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { IFeaturesMap, IRBushItem } from 'shared/map-search/types/map';
import { TMapBounds } from 'shared/map-search/types/mapBounds';
import { IApplicationState } from 'shared/map-search/types/redux';
import { getTilesToUpdate } from 'shared/map-search/utils/tiles';
import type { TPromoPinConfig } from 'shared/map-search/utils/ymapsModules/PinPulseManager';

import { useFeaturesContext } from '../context';

interface IFeaturesPropertiesUpdaterProps {
  promoPinConfig: TPromoPinConfig;
  promoDetailsVisible?: boolean;
}

export const FeaturesPropertiesUpdater: React.FC<IFeaturesPropertiesUpdaterProps> = ({
  promoPinConfig,
  promoDetailsVisible,
}) => {
  const { rbush, pointsLayer, balloonManager, balloonPromoManager } = useFeaturesContext();
  const { bounds, zoom } = useSelector<IApplicationState, TMapBounds>(state => state.mapBounds);
  const features = useSelector<IApplicationState, IFeaturesMap>(state =>
    state.features[state.mapBounds.precision] ? state.features[state.mapBounds.precision].features : {},
  );
  const prevFeaturesRef = React.useRef<IFeaturesMap>(features);
  const prevFeatures = prevFeaturesRef.current;

  const prevPromoDetailsVisibleRef = React.useRef<boolean | undefined>(promoDetailsVisible);
  const prevPromoDetailsVisible = prevPromoDetailsVisibleRef.current;

  React.useEffect(() => {
    const promoDetailsChanged = promoDetailsVisible !== prevPromoDetailsVisible;
    const updatedFeatures = Object.values(features).filter(({ id, properties }) => {
      const prevFeature = prevFeatures[id];
      if (!prevFeature) {
        return false;
      }
      /**
       * @todo разработка в MNZ-700
       * Параметр promoDetailsChanged изменяется на 17 зумме для отрисовки детальной информации
       */
      if (!equals(properties, prevFeature.properties) || promoDetailsChanged) {
        return true;
      }

      return false;
    });

    const rbushItemsToUpdate: IRBushItem[] = [];
    for (const feature of updatedFeatures) {
      /**
       * @todo разработка в MNZ-700
       * Если пин должен быть в промо-слое, то обновляем его свойства
       */
      if (!feature.properties.isExtended || feature.properties.specialPromo) {
        balloonManager.updateBalloonProperties(feature.id, feature.properties);
        balloonPromoManager.updateBalloonProperties(feature.id, feature.properties, promoDetailsVisible);
      }

      const featuresInRbush = rbush.search({
        minX: feature.geometry.coordinates[0],
        maxX: feature.geometry.coordinates[0],
        minY: feature.geometry.coordinates[1],
        maxY: feature.geometry.coordinates[1],
      });

      const featureInRbush = featuresInRbush.find(({ feature: { id } }) => id === feature.id);
      if (featureInRbush) {
        featureInRbush.feature.properties = feature.properties;
        rbushItemsToUpdate.push(featureInRbush);
      }
    }

    if (pointsLayer && rbushItemsToUpdate.length > 0) {
      const tilesToUpdate = getTilesToUpdate({
        rbushItems: rbushItemsToUpdate,
        bounds,
        zoom,
        includeShadow: true,
        includePulseRadius: promoPinConfig.promoPinsEnabled,
        pulseRadius: promoPinConfig.pulseRadius,
      });
      pointsLayer.layer.update(tilesToUpdate);
    }

    prevFeaturesRef.current = features;
    prevPromoDetailsVisibleRef.current = promoDetailsVisible;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, promoDetailsVisible]);

  return null;
};
